import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { TeamBadge, LiveIndicator } from "@/components/SportsCards";
import LiveCenter from "@/components/LiveCenter";

const toBalls = (o) => { const [a, b] = String(o || "0").split("."); return Number(a) * 6 + Number(b || 0); };
const chip = (b) => (b === "W" ? "bg-cr-danger text-white" : b === "6" ? "bg-cr-gold text-black" : b === "4" ? "bg-emerald-500 text-black" : b === "wd" || b === "nb" ? "bg-sky-500/40 text-sky-100" : "bg-white/10 text-cr-ink");
const EVENT = { W: ["WICKET!", "text-cr-danger"], 6: ["SIX!", "text-cr-gold"], 4: ["FOUR!", "text-emerald-400"] };
const BREAK_RE = /stump|lunch|tea|innings break|drinks|rain|bad light|delay|stops play|break/i;

const Stat = ({ k, v, cls = "" }) => <span className="rounded bg-white/5 px-2 py-0.5"><span className="text-cr-muted">{k}</span> <b className={cls}>{v}</b></span>;

const ExtDelta = ({ cur }) => {
  const prev = useRef(null);
  const [d, setD] = useState(null);
  const key = cur ? `${cur.team}|${cur.runs}|${cur.wkts}|${cur.overs}` : "";
  useEffect(() => {
    if (!cur) return;
    const p = prev.current;
    if (p && p.team === cur.team && key !== p.key) {
      setD({ runs: cur.runs - p.runs, balls: toBalls(cur.overs) - toBalls(p.overs), wkts: cur.wkts - p.wkts, at: new Date() });
    }
    prev.current = { ...cur, key };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="score-strip-ext-delta" className="mt-3 flex flex-wrap items-center gap-2 border-t border-cr-line pt-3 text-xs">
      {d ? <><span className="font-semibold text-cr-gold">Last update</span><Stat k="Runs" v={`+${d.runs}`} cls="text-cr-gold" /><Stat k="Balls" v={d.balls} /><Stat k="Wkts" v={d.wkts} cls="text-cr-danger" />
        <span className="text-cr-muted">at {d.at.toLocaleTimeString()}</span></>
        : <span className="text-cr-muted">Ball-by-ball not available for this match — score updates every few minutes.</span>}
    </div>
  );
};

const useCountdown = (start) => {
  const [t, setT] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setT(Date.now()), 1000); return () => clearInterval(i); }, []);
  const s = Math.max(0, Math.floor((new Date(start) - t) / 1000));
  return `${Math.floor(s / 86400)}d ${String(Math.floor((s % 86400) / 3600)).padStart(2, "0")}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m ${String(s % 60).padStart(2, "0")}s`;
};

const Upcoming = ({ m }) => {
  const left = useCountdown(m.start);
  return (
    <div data-testid="score-strip-upcoming" className="flex flex-wrap items-center justify-center gap-4 text-sm">
      <TeamBadge team={m.home} /><b>{m.home.short}</b><span className="text-cr-muted">vs</span><b>{m.away.short}</b><TeamBadge team={m.away} />
      <span className="rounded-full border border-cr-gold/40 px-3 py-1 font-mono text-cr-gold">{m.is_live ? (m.status || "Match about to start") : `Starts in ${left}`}</span>
    </div>
  );
};

const useStripData = (m) => {
  const [x, setX] = useState(null);
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (m.state === "upcoming") return undefined;
    const a = () => api.get(`/exchange/${m.id}`).then((r) => setX(r.data)).catch(() => {});
    const b = () => api.get(`/cricket/matches/${m.id}/live`).then((r) => setLive(r.data)).catch(() => {});
    a(); b();
    if (!m.is_live) return undefined;
    const i = setInterval(a, 5000), j = setInterval(b, 10000);
    return () => { clearInterval(i); clearInterval(j); };
  }, [m.id, m.state, m.is_live]);
  return { x, live };
};

const Players = ({ live, team }) => {
  if (!live?.batting?.length) return null;
  const last = live.commentary?.[0];
  const bats = live.batting.filter((b) => b.team_name === team && !b.is_out).slice(-2);
  const bowler = live.bowling.find((b) => b.player_name === (last?.bowler_name || live.current_bowler));
  return (
    <div data-testid="score-strip-players" className="grid gap-1 text-xs sm:text-sm">
      {bats.map((b) => (
        <p key={b.player_name} data-testid="strip-batter" className="flex items-center gap-2"><span className={b.player_name === last?.batsman_name ? "text-cr-gold" : "text-cr-ink/80"}>{b.player_name === last?.batsman_name ? "●" : "○"}</span>
          <span className="truncate font-semibold">{b.player_name}</span><span className="font-display font-bold">{b.score}</span><span className="text-cr-muted">({b.balls})</span>
          <span className="text-cr-muted">4s {b.fours} · 6s {b.sixes}</span></p>))}
      {bowler && <p data-testid="strip-bowler" className="text-cr-muted">Bowler <b className="text-cr-ink">{bowler.player_name}</b> {bowler.overs}-{bowler.maidens}-{bowler.runs}-{bowler.wickets}{bowler.wides ? ` · wd ${bowler.wides}` : ""}{bowler.no_balls ? ` · nb ${bowler.no_balls}` : ""}</p>}
    </div>
  );
};

const Flash = ({ last6 }) => {
  const [ev, setEv] = useState(null);
  const prev = useRef(null);
  const key = last6?.join(",");
  useEffect(() => {
    if (prev.current !== null && key !== prev.current) {
      const b = last6[last6.length - 1];
      if (EVENT[b]) { setEv({ b, id: Date.now() }); const t = setTimeout(() => setEv(null), 2500); prev.current = key; return () => clearTimeout(t); }
    }
    prev.current = key;
    return undefined;
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <AnimatePresence>{ev && (
      <motion.div key={ev.id} data-testid="score-strip-flash" initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.6 }} transition={{ duration: 0.35 }}
        className="pointer-events-none absolute inset-0 z-10 grid place-items-center bg-black/70 backdrop-blur-sm">
        <span className={`font-display text-5xl font-black italic tracking-wider sm:text-7xl ${EVENT[ev.b][1]}`}>{EVENT[ev.b][0]}</span>
      </motion.div>)}</AnimatePresence>
  );
};

export default function LiveScoreStrip({ m }) {
  const { x, live } = useStripData(m);
  const s = x?.score;
  const status = x?.match?.status || m.status;
  const onBreak = m.is_live && BREAK_RE.test(status || "");
  const sideOf = (team) => (team === m.home.name ? m.home : m.away);
  const cur = s?.innings?.[s.innings.length - 1];
  const prevInns = s?.innings?.slice(0, -1).slice(-2) || [];
  let need = null;
  if (s && x.format && s.innings.length === 2 && m.is_live) {
    const runs = s.innings[0].runs + 1 - cur.runs, balls = x.format * 6 - toBalls(cur.overs);
    if (runs > 0 && balls > 0) need = `${sideOf(cur.team).short} need ${runs} run${runs > 1 ? "s" : ""} from ${balls} ball${balls > 1 ? "s" : ""}`;
  }
  return (
    <div data-testid="live-score-strip" className="relative mb-6 overflow-hidden rounded-xl border border-cr-gold/40 bg-[#0b0c0c]/95 shadow-[0_8px_30px_rgba(0,0,0,.6)] backdrop-blur">
      <img src="/brand/cricket/hero.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-10" />
      <div className="relative">
        <div className="flex items-center justify-between gap-2 border-b border-cr-line bg-black/40 px-4 py-1.5 text-[11px] uppercase tracking-wider text-cr-muted">
          <span className="truncate">{m.league}</span>
          <span data-testid="score-strip-status" className="flex items-center gap-2">{m.is_live && <LiveIndicator />}<span className="normal-case">{status}</span></span>
        </div>
        {onBreak && <div data-testid="score-strip-break" className="bg-amber-500 px-4 py-1.5 text-center text-sm font-black uppercase tracking-wider text-black">Break · {status}</div>}
        <div className="px-4 py-3">
          {m.is_live && cur ? <><LiveCenter x={x} live={live} cur={cur} short={sideOf(cur.team).short} />{m.ext && <ExtDelta cur={cur} />}</> : m.state === "upcoming" || !cur ? <Upcoming m={m} /> : (
            <div className="grid items-center gap-4 md:grid-cols-[1.3fr_1fr_auto]">
              <div className="flex items-center gap-3">
                <TeamBadge team={sideOf(cur.team)} big />
                <div>
                  <p className="text-xs text-cr-muted">{cur.team}</p>
                  <motion.p key={`${cur.runs}-${cur.wkts}`} initial={{ y: -8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} data-testid="score-strip-score" className="font-display text-3xl font-black text-cr-gold sm:text-4xl">
                    {cur.runs}/{cur.wkts}<span className="ml-2 text-base font-bold text-cr-ink/70">({cur.overs})</span></motion.p>
                  <p className="text-xs text-cr-muted">CRR <b className="text-cr-ink">{s.crr}</b>{s.rrr != null && <> · RRR <b className="text-cr-ink">{s.rrr}</b></>}
                    {prevInns.map((i, k) => <span key={k} className="ml-2">· {sideOf(i.team).short} {i.runs}/{i.wkts}</span>)}</p>
                </div>
              </div>
              {m.ext && <Players live={live} team={cur.team} />}
              <div className="flex gap-1.5" data-testid="score-strip-last6">
                {s.last6.map((b, i) => (
                  <motion.span key={`${i}-${b}-${s.last6.join("")}`} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.05, type: "spring", stiffness: 400 }}
                    className={`grid h-8 w-8 place-items-center rounded-full text-xs font-black ${chip(b)}`}>{b}</motion.span>))}
              </div>
            </div>)}
          {need && !m.is_live && <p data-testid="score-strip-need" className="mt-2 text-center text-sm font-semibold text-cr-gold">{need}</p>}
          {!m.is_live && m.result && <p className="mt-2 text-center text-sm font-semibold text-cr-gold">{m.result}</p>}
        </div>
        {s && <Flash last6={s.last6} />}
      </div>
    </div>
  );
}
